import { useState } from 'react';
import { ActionableNotification } from '@carbon/react';
import { acknowledgeReview } from './api';
import { onlyArchivedReasons, reviewLines } from './utils';
import type { ReviewStatus } from './types';
import styles from './case-editor.module.scss';

export interface ReviewBannerProps {
  caseId: number;
  status: ReviewStatus;
  /** Reloads the case after an acknowledgement. */
  onAcknowledged: () => void;
  /** Reports a failed acknowledgement. */
  onError: (message: string) => void;
}

/**
 * Why the case needs review — one line per domain whose scenario changed or was retired since it
 * was last reviewed — with Acknowledge, which marks those domains reviewed as of what the banner
 * showed (its `asOf` and each domain's area-config ids), so a change made since still shows.
 * Nothing when the case needs no review.
 *
 * @param {ReviewBannerProps} props - The case, its review status and handlers.
 * @return {JSX.Element | null} The banner.
 */
export function ReviewBanner({
  caseId,
  status,
  onAcknowledged,
  onError,
}: ReviewBannerProps) {
  const [submitting, setSubmitting] = useState(false);
  if (!status.needsReview) return null;
  // Acknowledging cannot clear an archived scenario, so with nothing else to clear there is no
  // Acknowledge: the reasons say to reassign.
  const acknowledgeable = !onlyArchivedReasons(status);

  const acknowledge = async () => {
    setSubmitting(true);
    try {
      await acknowledgeReview(caseId, status);
      onAcknowledged();
    } catch (error) {
      onError(
        error instanceof Error
          ? error.message
          : 'Failed to acknowledge the review'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionableNotification
      className={styles.banner}
      inline
      kind="warning"
      // hasFocus off: its auto-focus scrolls the banner into view on load (page jump)
      hasFocus={false}
      lowContrast
      hideCloseButton
      title="This case needs review"
      actionButtonLabel={
        acknowledgeable
          ? submitting
            ? 'Acknowledging...'
            : 'Acknowledge'
          : undefined
      }
      onActionButtonClick={() => {
        if (acknowledgeable && !submitting) acknowledge();
      }}
    >
      <ul className={styles.reviewList}>
        {reviewLines(status).map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </ActionableNotification>
  );
}
